import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Matches parsing logic in ReferenceBackgroundCheckAuthorization.jsx
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseConsentFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.consent) {
    return result.consent;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  // Single consent object from API
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null || result.employeeFullName != null || result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * Print-ready HTML for Reference & Background Check Authorization (completed forms).
 * @param {Record<string, unknown>} consent API consent record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildReferenceBackgroundCheckPrintHtml(consent, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeFullName = escapeHtml(
    consent.employeeFullName || consent.applicantName || "",
  );
  const dateOfBirth = formatDate(consent.dateOfBirth);
  const ssn = escapeHtml(String(consent.socialSecurityNumber || ""));
  const currentAddress = escapeHtml(String(consent.currentAddress || ""));
  const position = escapeHtml(String(consent.position || ""));
  const homePhone = escapeHtml(String(consent.homePhone || ""));
  const cellPhone = escapeHtml(String(consent.cellPhone || ""));

  const applicantSignature = escapeHtml(String(consent.applicantSignature || ""));
  const applicantSignedAt = formatDate(consent.applicantSignedAt);
  const reviewedBySignedBy = escapeHtml(String(consent.reviewedBySignedBy || ""));
  const reviewedBySignature = escapeHtml(String(consent.reviewedBySignature || ""));
  const reviewedBySignedAt = formatDate(consent.reviewedBySignedAt);

  const previousEmployer = escapeHtml(String(consent.previousEmployer || ""));
  const employedFrom = formatDate(consent.employedFrom);
  const employedTo = formatDate(consent.employedTo);
  const additionalComments = escapeHtml(String(consent.additionalComments || ""));
  const hrRepresentative = escapeHtml(String(consent.hrRepresentative || ""));
  const hrTitle = escapeHtml(String(consent.hrTitle || ""));
  const hrDate = formatDate(consent.hrDate);

  const isCertified =
    !!(consent.applicantSignedAt || consent.applicantSignature) ||
    String(consent.status || "").toLowerCase() === "completed";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Reference &amp; Background Check — ${employeeFullName || "Applicant"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet {
      max-width: 880px;
      margin: 0 auto;
    }
    /* On-screen preview: paper-on-desk look (hidden when printing) */
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 13px; font-weight: 500; color: #2563eb; margin: 0; }
    h2.section-title {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 8px;
      margin: 20px 0 12px 0;
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 20px;
    }
    .grid-full { grid-column: 1 / -1; }
    .field-label {
      font-size: 10px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 4px;
    }
    .req::after { content: " *"; color: #dc2626; }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 38px;
      background: #fff;
      font-size: 12px;
      color: #111827;
    }
    .auth-box {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 16px;
      margin-top: 8px;
    }
    .auth-box p { margin: 0 0 10px 0; text-align: justify; font-size: 11px; color: #374151; }
    .auth-box p:last-child { margin-bottom: 0; }
    .cert-box {
      margin-top: 16px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 11px; letter-spacing: 0.02em; margin: 0 0 4px 0; }
    .cert-sub { font-size: 9px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 11px;
    }
    .chk { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 {
      font-size: 12px;
      font-weight: 700;
      margin: 0 0 10px 0;
      color: #111827;
    }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .footer-print {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
    .official {
      margin-top: 20px;
      padding-top: 12px;
      border-top: 2px dashed #9ca3af;
    }
    .official h3 { font-size: 12px; margin: 0 0 8px 0; }
    .row-inline { display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: baseline; margin-bottom: 6px; font-size: 11px; }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Reference &amp; Background Check Authorization</p>
        <p class="title-sub">Please complete the form below</p>
      </div>
    </div>

    <h2 class="section-title">Personal Information</h2>
    <div class="grid">
      <div>
        <div class="field-label req">Employee Full Name</div>
        <div class="field-value">${employeeFullName || "—"}</div>
      </div>
      <div>
        <div class="field-label req">Date of Birth</div>
        <div class="field-value">${escapeHtml(dateOfBirth) || "—"}</div>
      </div>
      <div>
        <div class="field-label req">Social Security Number</div>
        <div class="field-value">${ssn || "—"}</div>
      </div>
      <div></div>
      <div class="grid-full">
        <div class="field-label req">Current Address</div>
        <div class="field-value">${currentAddress || "—"}</div>
      </div>
      <div>
        <div class="field-label req">Position Applied For</div>
        <div class="field-value">${position || "—"}</div>
      </div>
      <div>
        <div class="field-label req">Home Phone</div>
        <div class="field-value">${homePhone || "—"}</div>
      </div>
      <div>
        <div class="field-label">Cell Phone</div>
        <div class="field-value">${cellPhone || "—"}</div>
      </div>
    </div>

    <h2 class="section-title">Authorization Statement</h2>
    <div class="auth-box">
      <p>
        I certify that the information provided by me is correct. I understand that false or misleading statements made herein, in connection with this application, may result in disqualification or dismissal from employment. I hereby authorize <strong>${company}</strong> and its agents to make a thorough investigation of my past employment and activities, including but not limited to verification of all data given on this application, related papers, or interviews. I agree to cooperate in such investigation and release all parties from all liability for any damage that may result from furnishing information to <strong>${company}</strong>. I authorize the procurement of a consumer report in connection with my application for employment and, if hired, periodically during my employment. I understand that this consumer report may include verification of my social security number, current and previous residences, employment history, education background, character references, drug testing, civil and criminal history records from any criminal justice agency in any or all federal, state, county jurisdictions, driving records, birth records, and any other public records.
      </p>
      <p>
        I further authorize any individual, company, firm, corporation, or public agency (including the Social Security Administration and law enforcement agencies) to divulge any and all information, verbal or written, pertaining to me to <strong>${company}</strong> or its authorized representative(s). I agree to furnish any additional information and complete any authorizations as may be required to complete this review. I further authorize the complete release of any records or data, including information received from other sources, which may be in their files or which may be obtained from other sources. This authorization and consent is given in advance of any such investigation, disclosure, or receipt of information and is intended to comply with any and all applicable federal and state laws.
      </p>
      <p>
        I hereby release, discharge, and exonerate <strong>${company}</strong>, the Social Security Administration, and its agents, officials, representatives, or assigned agencies (including officers, employees, or related personnel), both individually and collectively, from any and all liability for damages of whatever kind, which may at any time result to me, my heirs, family, or associates because of compliance with this authorization and request to release information, or any attempt to comply with it.
      </p>
    </div>

    <div class="cert-box">
      <p class="cert-title">• APPLICANT CERTIFICATION</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${isCertified ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee Section</h3>
        <div class="sig-label">EMPLOYEE FULL NAME</div>
        <div class="field-value">${employeeFullName || "—"}</div>
        <div class="sig-label">EMPLOYEE SIGNATURE</div>
        <div class="sig-line">${applicantSignature || "—"}</div>
        <div class="sig-label">DATE</div>
        <div class="sig-line">${escapeHtml(applicantSignedAt) || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / Reviewer Section</h3>
        <div class="sig-label">ADMIN FULL NAME</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">ADMIN SIGNATURE</div>
        <div class="sig-line">${reviewedBySignature || "—"}</div>
        <div class="sig-label">DATE</div>
        <div class="sig-line">${escapeHtml(reviewedBySignedAt) || "—"}</div>
      </div>
    </div>

    <div class="official">
      <h3>For Official Use Only</h3>
      <div class="row-inline"><strong>Previous Employer:</strong> ${previousEmployer || "—"}</div>
      <div class="row-inline">
        <strong>Employed from:</strong> ${escapeHtml(employedFrom) || "—"}
        &nbsp;&nbsp;<strong>to</strong>&nbsp;&nbsp;
        ${escapeHtml(employedTo) || "—"}
      </div>
      <div class="row-inline"><strong>Additional Comments:</strong> ${additionalComments || "—"}</div>
      <div class="row-inline"><strong>HR Representative:</strong> ${hrRepresentative || "—"}</div>
      <div class="row-inline"><strong>Title:</strong> ${hrTitle || "—"} &nbsp; <strong>Date:</strong> ${escapeHtml(hrDate) || "—"}</div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
